import Vue from 'vue';
import Vuex from 'vuex';
import {
  contractValidator, emailValidator, ICOValidator, odometerReadingValidator, required,
} from '@/validators';

import {
  japiFile, japiGet, japiPost, japiPostInspection,
} from '@/api';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    attachments: [],
    attachmentKey: 1,
    model: {
      serviceAvailability: true,
      isMobile: false,
      step: 1,
      lastStep: 1,
      isLoading: false,
      illustrationPreview: {
        show: false,
        type: null,
      },
      save: {
        successful: true,
        type: 'SAVE',
        attempted: false,
      },
    },
    odometerReading: null,
    insurerEmail: null,
    saveForLaterGDPR: false,
    errors: [],
    findInspection: {
      contractNumber: undefined,
      companyNumber: null,
      birthDate: undefined,
    },
    inspectionResponse: {},
  },
  getters: {
    getInspection: (state) => state.findInspection,
    getInspectionResponse: (state) => state.inspectionResponse,
    getPeriodTo: (state) => state.inspectionResponse.periodTo,
    getEmail: (state) => state.insurerEmail,
    getModel: (state) => state.model,
    getErrors: (state) => state.errors,
    getAttachments: (state) => state.attachments,
    getSaveForLaterGDPR: (state) => state.saveForLaterGDPR,
    getIllustrationPreviewStatus: (state) => state.model.illustrationPreview.show,
    getIllustrationPreviewType: (state) => state.model.illustrationPreview.type,
    getAttachmentsByKey: (state) => (key) => state.attachments.filter((item) => item.type === key),
  },
  mutations: {
    SET_FINDINSPECTION(state, newValue) {
      state.findInspection = {
        contractNumber: newValue.contractNumber || undefined,
        companyNumber: newValue.companyNumber || undefined,
        birthDate: newValue.birthDate || undefined,
      };
    },
    SET_CONTRACTNUMBER(state, newValue) {
      state.findInspection.contractNumber = newValue;
    },
    SET_COMPANYNUMBER(state, newValue) {
      state.findInspection.companyNumber = newValue;
    },
    SET_BIRTHDATE(state, newValue) {
      state.findInspection.birthDate = newValue;
    },
    SET_SERVICEAVAILABILITY(state, newValue) {
      state.model.serviceAvailability = newValue;
    },
    SET_LOADING(state, newValue) {
      state.model.isLoading = newValue;
    },
    SET_STEP(state, newValue) {
      state.model.step = newValue;
    },
    SET_LASTSTEP(state, newValue) {
      state.model.lastStep = newValue;
    },
    SET_ISMOBILE(state, newValue) {
      state.model.isMobile = newValue;
    },
    SET_ATTEMPTED(state, newValue) {
      state.model.save.attempted = newValue;
    },
    SET_ILLUSTRATIONPREVIEW(state, newValue) {
      state.model.illustrationPreview.show = newValue;
    },
    SET_ILLUSTRATIONPREVIEW_TYPE(state, newValue) {
      state.model.illustrationPreview.type = newValue;
    },
    SET_ERRORS(state, newValue) {
      state.errors = newValue;
    },
    DEL_ERROR(state, newValue) {
      const err = state.errors;
      err.splice(err.indexOf(newValue), 1);
      state.errors = [...state.errors];
    },
    SET_INSPECTIONRESPONSE(state, newValue) {
      state.inspectionResponse = newValue;
    },
    SET_INSUREREMAIL(state, newValue) {
      state.insurerEmail = newValue;
    },
    SET_ODOMETER_READING(state, newValue) {
      state.odometerReading = newValue;
    },
    SET_SAVEFORLATERGDPR(state, newValue) {
      state.saveForLaterGDPR = newValue;
    },
    RESET_ATTACHMENTS(state, newValue) {
      state.attachments[newValue.type] = [];
    },
    SET_ATTACHMENT_HASH(state, uploadedFile) {
      state.attachments = state.attachments.map((item) => {
        if (item.id === uploadedFile.id) {
          item.hash = uploadedFile.hash;
        }
        return item;
      });
    },
    ADD_ATTACHMENT(state, newValue) {
      newValue.id = state.attachmentKey;
      state.attachmentKey += 1;
      state.attachments.push(newValue);
    },
    REMOVE_ATTACHMENT(state, newValue) {
      state.attachments = state.attachments.filter((item) => item.id !== newValue.id);
    },
    SET_SAVE(state, value) {
      state.model.save[value.key] = value.value;
    },
  },
  actions: {
    picturesToUpload({ state }) {
      const picturesToUpload = [];
      state.attachments.forEach(async (requiredFile) => {
        if (!requiredFile.hash) {
          const fd = new FormData();
          fd.append('picture', requiredFile.file);
          picturesToUpload.push(japiFile('IPzpUploadApi/savePicture', fd).then((data) => ({
            hash: data, id: requiredFile.id,
          })));
        }
      });
      return picturesToUpload;
    },

    async findInspection({ commit, state }) {
      commit('SET_LOADING', true);
      const json = await japiPostInspection('IPzpUploadApi/findInspection', [state.findInspection]);
      commit('SET_INSPECTIONRESPONSE', json);
      commit('SET_LOADING', false);
      if (json === 'BP001') {
        throw Error(json);
      }
    },
    async saveForLater({ state, commit, dispatch }) {
      commit('SET_LOADING', true);
      commit('SET_SAVE', { key: 'type', value: 'FORLATER' });
      commit('DEL_ERROR', 'FE008');
      commit('DEL_ERROR', 'FE015');
      const err = [];
      if (!state.saveForLaterGDPR) {
        err.push('FE020');
      }
      if (!required(state.insurerEmail)) {
        err.push('FE021');
      }
      await dispatch('validateEmail');
      const invalidFiles = await dispatch('areFilesInvalid');
      if (invalidFiles) {
        err.push('FE014');
      }
      if (err.length !== 0 || state.errors.length !== 0) {
        commit('SET_ERRORS', [...state.errors, ...err]);
        commit('SET_LOADING', false);
        return false;
      }
      // BEGIN UPLOAD
      commit('SET_ERRORS', err);
      try {
        const uploadPictures = await Promise.all(await dispatch('picturesToUpload'));
        uploadPictures.forEach((uploadedFile) => {
          if (uploadedFile.hash !== 'ERR' && uploadedFile.hash !== 'BP010') {
            commit('SET_ATTACHMENT_HASH', uploadedFile);
          }
        });
        const saveForLaterData = {
          model: {
            step: state.model.step,
            lastStep: state.model.lastStep,
            isMobile: state.model.isMobile,
          },
          saveForLaterGDPR: state.saveForLaterGDPR,
          insurerEmail: state.insurerEmail,
          odometerReading: state.odometerReading,
          findInspectionRequest: state.findInspection,
          inspectionResponse: state.inspectionResponse,
          attachments: state.attachments.map(({ hash, type, fileName }) => ({ hash, type, fileName })),
        };
        await japiPost('IPzpUploadApi/saveForLater', [saveForLaterData]);
        commit('SET_SAVE', { key: 'successful', value: true });
        commit('SET_LOADING', false);
        return true;
      } catch (e) {
        commit('SET_SAVE', { key: 'successful', value: false });
        commit('SET_LOADING', false);
        throw Error(e);
      }
    },
    async saveFiles({ commit, state, dispatch }) {
      commit('SET_LOADING', true);
      commit('SET_SAVE', { key: 'type', value: 'SAVE' });
      try {
        const response = await Promise.all(await dispatch('picturesToUpload'));
        response.forEach((uploadedFile) => {
          commit('SET_ATTACHMENT_HASH', uploadedFile);
        });
        const saveRequest = {
          ...state.findInspection,
          email: state.insurerEmail,
          odometerReading: state.odometerReading,
          pictures: state.attachments.map(({ hash, type }) => ({ hash, type })),
        };
        await japiPost('IPzpUploadApi/saveInspection', [saveRequest]);
        commit('SET_SAVE', { key: 'successful', value: true });
        commit('SET_LOADING', false);
      } catch (e) {
        commit('SET_SAVE', { key: 'successful', value: false });
        commit('SET_LOADING', false);
        throw Error(e);
      }
    },
    async loadInspection({ commit, dispatch }, inspection) {
      try {
        commit('SET_FINDINSPECTION', inspection.findInspectionRequest);
        await dispatch('findInspection', inspection.findInspectionRequest);
        commit('SET_INSPECTIONRESPONSE', inspection.inspectionResponse);
        inspection.attachments.forEach((file) => {
          commit('ADD_ATTACHMENT', file);
        });
        commit('SET_INSUREREMAIL', inspection.insurerEmail);
        commit('SET_ODOMETER_READING', inspection.odometerReading);
        commit('SET_STEP', inspection.model.step);
        commit('SET_LASTSTEP', inspection.model.lastStep);
        commit('SET_ISMOBILE', inspection.model.isMobile);
        commit('SET_SAVEFORLATERGDPR', inspection.saveForLaterGDPR);
        return inspection.model.step;
      } catch (e) {
        throw Error(e);
      }
    },
    isOdometerRequired({ state }) {
      const isRequired = state.inspectionResponse.inspectionPhotos.filter((item) => item.type === 'clock' && item.showOdometer);
      if (isRequired.length !== 0) {
        return true;
      }
      return false;
    },
    async validateOdometerReading({ state, commit, dispatch }) {
      const isRequired = await dispatch('isOdometerRequired');
      if (isRequired && (!required(state.odometerReading) || !odometerReadingValidator(state.odometerReading))) {
        if (!state.errors.includes('FE022')) {
          commit('SET_ERRORS', [...state.errors, 'FE022']);
        }
      } else if (state.errors.includes('FE022')) {
        commit('DEL_ERROR', 'FE022');
      }
    },
    async validateContractNumber({ state, commit }) {
      if (!required(state.findInspection.contractNumber) || !contractValidator(state.findInspection.contractNumber)) {
        if (!state.errors.includes('FE002')) {
          commit('SET_ERRORS', [...state.errors, 'FE002']);
        }
      } else if (state.errors.includes('FE002')) {
        commit('DEL_ERROR', 'FE002');
      }
    },
    async validateCompanyNumber({ state, commit }) {
      if (state.findInspection.companyNumber !== undefined && (!required(state.findInspection.companyNumber) || !ICOValidator(state.findInspection.companyNumber))) {
        if (!state.errors.includes('FE006')) {
          commit('SET_ERRORS', [...state.errors, 'FE006']);
        }
      } else if (state.errors.includes('FE006')) {
        commit('DEL_ERROR', 'FE006');
      }
    },
    async validateBirthDate({ state, commit }) {
      if (state.findInspection.birthDate !== undefined
        && (!state.findInspection.birthDate || !/^\s*((?:19|20)\d{2})-(1[012]|0?[1-9])-(3[01]|[12][0-9]|0?[1-9])\s*$/.test(state.findInspection.birthDate))) {
        if (!state.errors.includes('FE004')) {
          commit('SET_ERRORS', [...state.errors, 'FE004']);
        }
      } else if (state.errors.includes('FE004')) {
        commit('DEL_ERROR', 'FE004');
      }
    },
    async validateEmail({ state, commit }) {
      if (!emailValidator(state.insurerEmail)) {
        if (!state.errors.includes('FE007')) {
          commit('SET_ERRORS', [...state.errors, 'FE007']);
        }
      } else if (state.errors.includes('FE007')) {
        commit('DEL_ERROR', 'FE007');
        if (state.errors.includes('FE008')) {
          commit('DEL_ERROR', 'FE008');
        }
        if (state.errors.includes('FE021')) {
          commit('DEL_ERROR', 'FE021');
        }
      }
    },
    areFilesInvalid({ state }) {
      let areFilesInvalid = false;
      state.attachments.forEach((file) => {
        if (file.maxSizeError || file.formatError || file.resolutionError) {
          areFilesInvalid = true;
        }
      });
      return areFilesInvalid;
    },
    async validateFiles({ state, commit, dispatch }, checkFor = {}) {
      checkFor.allFiles = checkFor.allFiles || false;
      checkFor.errors = checkFor.errors || true;

      const requiredKeys = state.inspectionResponse.inspectionPhotos.map((requiredFile) => requiredFile.type).filter((key) => key !== 'damages' && key !== 'security' && key !== 'additionalEquipment');
      // Check if all files needed are present
      let needMoreFiles = false;
      requiredKeys.forEach((key) => {
        if (state.attachments.filter((item) => item.type === key).length === 0) {
          needMoreFiles = true;
        }
      });
      if (needMoreFiles && checkFor.allFiles) {
        if (!state.errors.includes('FE015')) {
          commit('SET_ERRORS', [...state.errors, 'FE015']);
        }
      } else if (state.errors.includes('FE015') && checkFor.allFiles) {
        commit('DEL_ERROR', 'FE015');
      }
      // Check if there are any errors in files
      const invalidFiles = await dispatch('areFilesInvalid');
      if (invalidFiles && checkFor.errors) {
        if (!state.errors.includes('FE014')) {
          commit('SET_ERRORS', [...state.errors, 'FE014']);
        }
      } else if (state.errors.includes('FE014') && checkFor.errors) {
        commit('DEL_ERROR', 'FE014');
      }
    },
    async validateStep({ state, commit, dispatch }) {
      const err = [];
      if (state.model.step === 1) {
        if (state.inspectionResponse === 'BP001') {
          err.push('FE001');
        }
        if (state.inspectionResponse === 'BP003') {
          err.push('FE003');
        }
        if (state.inspectionResponse === 'BP005') {
          err.push('FE005');
        }
        dispatch('validateContractNumber');
        dispatch('validateBirthDate');
        dispatch('validateCompanyNumber');
      }
      if (state.model.step === 3) {
        if (state.errors.includes('FE020')) {
          commit('DEL_ERROR', 'FE020');
        }
        if (state.errors.includes('FE021')) {
          commit('DEL_ERROR', 'FE021');
        }
        if (!required(state.insurerEmail)) {
          err.push('FE008');
        } else if (state.errors.includes('FE008')) {
          commit('DEL_ERROR', 'FE008');
        }
        dispatch('validateOdometerReading');
        dispatch('validateEmail');
        dispatch('validateFiles', { allFiles: true });
      }
      commit('SET_ERRORS', [...state.errors, ...err]);
    },
    async checkServiceAvailability({ commit }) {
      try {
        const availability = await japiGet('IPzpUploadApi/checkServiceAvailability');
        if (availability === 'ERR') {
          commit('SET_SERVICEAVAILABILITY', false);
        }
      } catch {
        commit('SET_SERVICEAVAILABILITY', false);
      }
    },
  },
  modules: {},
});
